import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { Stage, TimelineEvent } from "../../api/types";
import { cx, errorMessage, formatUsd, str } from "../../lib/format";
import { CI_META } from "../../lib/status";
import { AttachmentList } from "../Attachments";
import { Icon, type IconName } from "../Icon";
import { Markdown } from "../Markdown";
import { MarkdownEditor } from "../MarkdownEditor";
import { Pill, StatusPill } from "../Pill";
import { RelativeTime } from "../RelativeTime";
import { EditedMarker } from "./Revisions";
import { Transcript } from "./Transcript";

const BOX_KINDS = new Set([
  "comment",
  "agent_message",
  "agent_question",
  "review_decision",
  "stage_completed",
  "gate_result",
]);

const ICONS: Record<string, IconName> = {
  created: "plus",
  comment: "comment",
  definition_updated: "pencil",
  stage_changed: "arrowRight",
  status_changed: "dot",
  claimed: "bot",
  released: "bot",
  run_started: "play",
  run_finished: "stop",
  agent_message: "bot",
  agent_question: "question",
  stage_completed: "check",
  gate_started: "gate",
  gate_result: "gate",
  review_decision: "eye",
  proposal_created: "lightbulb",
  pr_opened: "pr",
  ci_status: "refresh",
  merged: "merge",
  post_merge_check: "refresh",
  env_event: "server",
  attention: "alert",
};

function badgeTone(ev: TimelineEvent): string {
  const d = ev.data;
  switch (ev.kind) {
    case "attention":
      return "tone-red";
    case "merged":
      return "tone-purple";
    case "stage_completed":
      return "tone-green";
    case "gate_result":
      return str(d.verdict) === "pass" ? "tone-green" : str(d.verdict) === "fail" ? "tone-red" : "";
    case "review_decision":
      return str(d.decision) === "approve" ? "tone-green" : "tone-red";
    case "ci_status":
    case "post_merge_check":
      return str(d.state) === "success" ? "tone-green" : str(d.state) === "failure" ? "tone-red" : "tone-yellow";
    case "agent_question":
      return "tone-yellow";
    default:
      return "";
  }
}

function gateName(g: unknown): string {
  if (typeof g === "string") return g;
  if (g && typeof g === "object") {
    const o = g as { name?: string; type?: string };
    return o.name || o.type || "gate";
  }
  return "gate";
}

export function TimelineItem({
  ev,
  stages,
  meId,
  pid,
}: {
  ev: TimelineEvent;
  stages: Stage[];
  meId: string;
  pid: string;
}) {
  const icon = ICONS[ev.kind] ?? "dot";
  const isBox = BOX_KINDS.has(ev.kind) && (ev.kind !== "gate_result" || !!ev.body);
  return (
    <li className={cx("tl-item", isBox ? "tl-item-box" : "tl-item-line")}>
      <span className={cx("tl-badge", badgeTone(ev))} aria-hidden="true">
        <Icon name={icon} size={13} />
      </span>
      {isBox ? <BoxEvent ev={ev} meId={meId} /> : <LineEvent ev={ev} stages={stages} pid={pid} />}
    </li>
  );
}

function verbFor(ev: TimelineEvent): ReactNode {
  const d = ev.data;
  switch (ev.kind) {
    case "comment":
      return "commented";
    case "agent_message":
      return "posted a message";
    case "agent_question":
      return "asked a question";
    case "stage_completed":
      return "completed the stage";
    case "gate_result":
      return (
        <>
          reviewed ({gateName(d.gate)}){" "}
          {str(d.verdict) && <Pill color={str(d.verdict) === "pass" ? "green" : "red"}>{str(d.verdict)}</Pill>}
        </>
      );
    case "review_decision":
      return str(d.decision) === "approve" ? (
        <>
          <Pill color="green">approved</Pill>
          {str(d.advance_to) && <span className="muted"> and skipped ahead</span>}
        </>
      ) : (
        <>
          <Pill color="red">requested changes</Pill>
          {str(d.send_back_to) && <span className="muted"> and sent back to {str(d.send_back_to)}</span>}
        </>
      );
    default:
      return ev.kind.replace(/_/g, " ");
  }
}

function BoxEvent({ ev, meId }: { ev: TimelineEvent; meId: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ev.body ?? "");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const edit = useMutation({
    meta: { inlineError: true },
    mutationFn: (body: string) => api.editComment(ev.id, body),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: qk.timeline(ev.task_id) });
      qc.invalidateQueries({ queryKey: qk.revisions(ev.id) });
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const canEdit = ev.kind === "comment" && ev.actor.type === "user" && ev.actor.id === meId;
  const isAgent = ev.actor.type === "agent" || ev.actor.type === "assistant";
  const summary = ev.kind === "stage_completed" ? str(ev.data.summary) : undefined;
  const diffStat = ev.kind === "stage_completed" ? str(ev.data.diff_stat) : undefined;

  return (
    <div className={cx("tl-box", ev.kind === "agent_question" && "tl-box-question")}>
      <div className="tl-box-header">
        <span className={cx("avatar avatar-sm", isAgent && "avatar-agent")} aria-hidden="true">
          {isAgent ? <Icon name="bot" size={12} /> : ev.actor.name.slice(0, 1).toUpperCase()}
        </span>
        <ActorName actor={ev.actor} />
        <span className="muted">{verbFor(ev)}</span>
        <span className="muted">
          <RelativeTime iso={ev.created_at} />
        </span>
        {ev.edited_at && <EditedMarker eventId={ev.id} editedAt={ev.edited_at} />}
        <span className="spacer" />
        {canEdit && !editing && (
          <button
            className="btn-icon"
            title="Edit comment"
            aria-label="Edit comment"
            onClick={() => {
              setDraft(ev.body ?? "");
              setError(null);
              setEditing(true);
            }}
          >
            <Icon name="pencil" size={13} />
          </button>
        )}
      </div>
      <div className="tl-box-body">
        {editing ? (
          <div>
            <MarkdownEditor value={draft} onChange={setDraft} rows={5} autoFocus onSubmitShortcut={() => edit.mutate(draft)} />
            {error && <div className="error-box">{error}</div>}
            <div className="row-actions">
              <button className="btn btn-sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn btn-sm btn-primary" disabled={edit.isPending} onClick={() => edit.mutate(draft)}>
                Update comment
              </button>
            </div>
          </div>
        ) : (
          <>
            {summary && summary !== ev.body && <Markdown>{summary}</Markdown>}
            {ev.body ? (
              <Markdown>{ev.body}</Markdown>
            ) : (
              !summary && ev.kind !== "review_decision" && <p className="muted">No description.</p>
            )}
            {diffStat && <pre className="diff-stat">{diffStat}</pre>}
          </>
        )}
        <AttachmentList attachments={ev.attachments} />
      </div>
    </div>
  );
}

/** "Alice", or "Claude Desktop · assistant for Alice" for the board MCP. */
function ActorName({ actor }: { actor: TimelineEvent["actor"] }) {
  if (actor.type !== "assistant") return <strong>{actor.name}</strong>;
  return (
    <span className="tl-assistant" title={`AI assistant acting for ${actor.on_behalf_of ?? "a user"} (board MCP token “${actor.name}”)`}>
      <strong>{actor.name}</strong>
      <span className="tl-assistant-tag">assistant for {actor.on_behalf_of ?? "a user"}</span>
    </span>
  );
}

function StageName({ stages, k }: { stages: Stage[]; k: unknown }) {
  const key = str(k);
  if (!key) return <strong>?</strong>;
  return <strong>{stages.find((s) => s.key === key)?.name ?? key}</strong>;
}

function LineEvent({ ev, stages, pid }: { ev: TimelineEvent; stages: Stage[]; pid: string }) {
  const d = ev.data;
  const actor = <ActorName actor={ev.actor} />;
  const runId = str(d.run_id);
  let text: ReactNode;
  switch (ev.kind) {
    case "created":
      text = <>{actor} created the task</>;
      break;
    case "definition_updated": {
      const fields = Array.isArray(d.fields) ? (d.fields as unknown[]).map(String).join(", ") : "the definition";
      text = (
        <>
          {actor} updated {fields.replace(/_/g, " ")}
        </>
      );
      break;
    }
    case "stage_changed": {
      const reason = str(d.reason);
      const verb =
        reason === "send_back"
          ? "sent the task back from"
          : reason === "manual"
            ? "moved the task from"
            : reason === "skip"
              ? "skipped ahead from"
              : "advanced from";
      text = (
        <>
          {actor} {verb} <StageName stages={stages} k={d.from} /> to <StageName stages={stages} k={d.to} />
        </>
      );
      break;
    }
    case "status_changed":
      text = (
        <>
          {actor} changed status {str(d.from) && <StatusPill status={str(d.from)!} />} →{" "}
          {str(d.to) && <StatusPill status={str(d.to)!} />}
          {str(d.reason) && <span className="muted"> · {str(d.reason)}</span>}
        </>
      );
      break;
    case "claimed":
      text = (
        <>
          <strong>{str(d.agent_type) ?? ev.actor.name}</strong> agent claimed the task
        </>
      );
      break;
    case "released":
      text = (
        <>
          {actor} released the task{str(d.reason) && <span className="muted">: {str(d.reason)}</span>}
        </>
      );
      break;
    case "run_started":
      text = (
        <>
          <strong>{str(d.agent_type) ?? ev.actor.name}</strong> started a {str(d.kind)?.replace(/_/g, " ") ?? ""} run
        </>
      );
      break;
    case "run_finished":
      text = (
        <>
          Run <Pill color={str(d.status) === "completed" ? "green" : str(d.status) === "failed" ? "red" : "gray"}>{str(d.status) ?? "finished"}</Pill>
          {typeof d.cost_usd === "number" && <span className="muted"> · {formatUsd(d.cost_usd)}</span>}
        </>
      );
      break;
    case "gate_started":
      text = (
        <>
          Gate <strong>{gateName(d.gate)}</strong> started
        </>
      );
      break;
    case "gate_result":
      text = (
        <>
          Gate <strong>{gateName(d.gate)}</strong>{" "}
          <Pill color={str(d.verdict) === "pass" ? "green" : "red"}>{str(d.verdict) ?? "done"}</Pill>
        </>
      );
      break;
    case "proposal_created":
      text = (
        <>
          {actor} proposed a follow-up task:{" "}
          <Link to={`/p/${pid}/proposals`}>{str(d.title) ?? "proposal"}</Link>
        </>
      );
      break;
    case "pr_opened":
      text = (
        <>
          {actor} opened{" "}
          <a href={str(d.url)} target="_blank" rel="noopener noreferrer">
            PR #{str(d.number)}
          </a>
        </>
      );
      break;
    case "ci_status": {
      const st = str(d.state);
      const meta = st && st in CI_META ? CI_META[st as keyof typeof CI_META] : null;
      text = (
        <>
          {meta ? <Pill color={meta.color}>{meta.label}</Pill> : <>CI {st}</>}
          {str(d.details) && <span className="muted"> · {str(d.details)}</span>}
        </>
      );
      break;
    }
    case "merged":
      text = (
        <>
          {actor} merged <code className="code-inline">{(str(d.sha) ?? "").slice(0, 7)}</code>
        </>
      );
      break;
    case "post_merge_check":
      text = (
        <>
          Post-merge check on base branch:{" "}
          <Pill color={str(d.state) === "success" ? "green" : str(d.state) === "failure" ? "red" : "yellow"}>
            {str(d.state)}
          </Pill>
        </>
      );
      break;
    case "env_event":
      text = (
        <>
          Environment <strong>{(str(d.state) ?? "").replace(/_/g, " ")}</strong>
          {str(d.message) && <span className="muted"> · {str(d.message)}</span>}
        </>
      );
      break;
    case "attention":
      text = (
        <span className="attention-inline">
          Needs attention{str(d.reason) && <>: {str(d.reason)}</>}
        </span>
      );
      break;
    default:
      text = (
        <>
          {actor} <span className="muted">{ev.kind.replace(/_/g, " ")}</span>
        </>
      );
  }

  const showTranscript = (ev.kind === "run_started" || ev.kind === "run_finished") && runId;

  return (
    <div className="tl-line">
      <div className="tl-line-text">
        {text}
        <span className="muted tl-time">
          {" · "}
          <RelativeTime iso={ev.created_at} />
        </span>
      </div>
      {ev.body && (
        <div className="tl-line-body">
          <Markdown>{ev.body}</Markdown>
        </div>
      )}
      {ev.attachments.length > 0 && <AttachmentList attachments={ev.attachments} />}
      {showTranscript && ev.kind === "run_started" && <TranscriptToggle runId={runId} />}
    </div>
  );
}

function TranscriptToggle({ runId }: { runId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="tl-transcript">
      <button className="link-button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={12} /> {open ? "Hide transcript" : "View transcript"}
      </button>
      {open && <Transcript runId={runId} />}
    </div>
  );
}
